import React from 'react'
import { Large, Medium } from '@/components/ui/typography'
import { cn } from '@/lib/utils'

interface NoResultsMessageProps {
    /**
     * The main message to display (e.g., "No products found", "No collections found")
     */
    message: string
    /**
     * Optional search term to include in the message. Only appended if truthy and non-empty.
     */
    searchTerm?: string
    /**
     * Optional suggestion text to display below the main message
     */
    suggestion?: string
    /**
     * Optional className for the container
     */
    className?: string
}

/**
 * NoResultsMessage Component
 *
 * A reusable component for displaying "No results found" messages in search panels.
 * Displays a centered message with optional search term and suggestion text.
 *
 * @example
 * <NoResultsMessage
 *   message="No products found"
 *   searchTerm="laptop"
 *   suggestion="Try searching with a different term"
 * />
 *
 * @example
 * <NoResultsMessage
 *   message="No products currently selected."
 *   suggestion="Grid will display all products in the Storefront product's section."
 * />
 */
export const NoResultsMessage: React.FC<NoResultsMessageProps> = ({
    message,
    searchTerm,
    suggestion,
    className,
}) => {
    const displayMessage = searchTerm?.trim()
        ? `${message} matching "${searchTerm.trim()}"`
        : message

    return (
        <div
            className={cn(
                'flex w-full flex-col items-center rounded-xl bg-secondary py-12',
                className,
            )}
            role="status"
            aria-live="polite"
            aria-label={displayMessage}
        >
            <Large className="mb-2 text-muted-foreground">
                {displayMessage}
            </Large>
            {suggestion && (
                <Medium className="mb-4 italic text-muted-foreground">
                    {suggestion}
                </Medium>
            )}
        </div>
    )
}
